import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
  Tooltip,
} from "recharts";
import Skeleton from "react-loading-skeleton";
import "./Activity.css";
import { authService } from "../../../../utils/authService";
import { useApiLoading } from "../StudentDashboard";

// Define TooltipProps manually to avoid import issues
interface TooltipProps<T, U> {
  active?: boolean;
  payload?: Array<{
    value: T;
    dataKey: string;
    color: string;
    payload: U;
  }>;
  label?: string;
}

interface DataItem {
  date: string;
  day_name: string;
  hours: string; // Format: "H:MM" or "HH:MM" - kept as string
  isUpcoming?: boolean;
  isCurrent?: boolean;
}

interface ActivityResponse {
  hours: DataItem[];
  weekly_limit: number;
  daily_limit: number;
}

// Convert time string (e.g., "1:49") to decimal hours for chart rendering
const convertTimeStringToHours = (timeString: string): number => {
  if (!timeString || timeString === "0:00") return 0;
  const [hours, minutes] = timeString.split(":").map(Number);
  return (hours || 0) + (minutes || 0) / 60;
};

// Sum time strings and return total as string (e.g., "1:49" + "3:33" = "5:22")
const sumTimeStrings = (timeStrings: string[]): string => {
  const totalMinutes = timeStrings.reduce((acc, timeStr) => {
    if (!timeStr || timeStr === "0:00") return acc;
    const [hours, minutes] = timeStr.split(":").map(Number);
    return acc + (hours || 0) * 60 + (minutes || 0);
  }, 0);
  
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${minutes.toString().padStart(2, "0")}`;
};

const CustomTooltip = ({ active, payload }: TooltipProps<number, DataItem>) => {
  if (!active || !payload?.length) return null;
  
  const entry = payload[0]?.payload as unknown as DataItem;
  if (entry.isUpcoming) return null;
  
  return (
    <div className="custom-tooltip" style={{ backgroundColor: "transparent", border: "none", padding: "2px", fontSize: "14px", color: "#000", cursor: "progress" }}>
      <p>{entry.hours}</p>
    </div>
  );
};

const Activity: React.FC = () => {
  const [data, setData] = useState<DataItem[] | null>(null);
  const [minThreshold, setMinThreshold] = useState(0);
  const [weeklyLimit, setWeeklyLimit] = useState(0);
  const [selectedWeek, setSelectedWeek] = useState(0);
  const { studentId, isCoursesApiLoaded } = useApiLoading();
  
  // State for activity data
  const [activityData, setActivityData] = useState<ActivityResponse | null>(null);
  const [weeklyData, setWeeklyData] = useState<ActivityResponse | null>(null);

  // Fetch activity data
  useEffect(() => {
    const fetchActivityData = async () => {
      if (!isCoursesApiLoaded || !studentId) return;
      
      try {
        const response = await authService.get(`coordinator/studentdashboard/hourspent/${studentId}/n/`);
        setActivityData(response.data);
      } catch (err) {
        console.error('Error fetching activity data:', err);
      }
    };

    fetchActivityData();
  }, [isCoursesApiLoaded, studentId]);

  // Fetch weekly data
  useEffect(() => {
    const fetchWeeklyData = async () => {
      if (!isCoursesApiLoaded || !studentId || selectedWeek <= 0) return;
      
      try {
        const response = await authService.get(`coordinator/studentdashboard/hourspent/${studentId}/${selectedWeek}/`);
        setWeeklyData(response.data);
      } catch (err) {
        console.error('Error fetching weekly data:', err);
      }
    };

    fetchWeeklyData();
  }, [isCoursesApiLoaded, studentId, selectedWeek]);

  useEffect(() => {
    if (activityData) {
      setData(activityData.hours);
      setWeeklyLimit(activityData.weekly_limit);
      setMinThreshold(activityData.daily_limit);
      setSelectedWeek(activityData.weekly_limit);
    }
  }, [activityData]);

  useEffect(() => {
    if (weeklyData && selectedWeek > 0) {
      setData(weeklyData.hours);
      setMinThreshold(weeklyData.daily_limit);
    }
  }, [weeklyData, selectedWeek]);

  const total = data
    ? sumTimeStrings(data.map((item) => item.hours))
    : "0:00";

  const getHoursOrThreshold = (entry: DataItem): number => {
    if (entry.isUpcoming) {
      return minThreshold;
    }
    return convertTimeStringToHours(entry.hours);
  };

  const getBarColor = (entry: DataItem): string => {
    if (entry.isUpcoming) return "#E5E5E5";
    if (entry.isCurrent) return "#8B00FF";
    if (entry.hours === "0:00") return "#E5E5E5";
    return "#E7C6FF";
  };

  return (
    <div className="p-0" style={{ fontFamily: "Arial, sans-serif" }}>
      <div className="bg-white rounded-2 p-2">
        <div className="p-2">
          <span
            style={{
              fontWeight: "bolder",
              fontSize: "20px",
              marginBottom: "10px",
            }}
          >
            Activity
          </span>
          <select
            className="float-end p-1"
            style={{ cursor: "pointer", fontSize: "10px" }}
            value={selectedWeek}
            onChange={(e) => setSelectedWeek(Number(e.target.value))}
          >
            {weeklyLimit > 0 ? (
              Array.from({ length: weeklyLimit }, (_, i) => i + 1).map(
                (week) => (
                  <option key={week} value={week}>
                    Week {week}
                  </option>
                )
              )
            ) : (
              <option value={0}>No weeks available</option>
            )}
          </select>
        </div>
        <span className="ms-3">
          {data ? `${total} hrs` : <Skeleton width={50} />}
        </span>

        <ResponsiveContainer width="100%" height={178}>
          {data ? (
            <BarChart
              data={data}
              margin={{ top: 10, right: 20, bottom: 0, left: 0 }}
            >
              <XAxis
                dataKey="day_name"
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={{ stroke: "white" }}
              />
              <Tooltip content={<CustomTooltip />} cursor={false} />
              <Bar
                dataKey={(entry) => getHoursOrThreshold(entry)}
                barSize={45}
                radius={[8, 8, 8, 8]}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${entry.hours}`} fill={getBarColor(entry)} />
                ))}
              </Bar>
              <ReferenceLine
                y={minThreshold}
                stroke="black"
                strokeDasharray="3 3"
                label={({ viewBox }) => (
                  <g
                    transform={`translate(${viewBox.width - 320}, ${
                      viewBox.y - 10
                    })`}
                  >
                    <rect width={60} height={20} fill="black" rx={5} />
                    <text
                      x={30}
                      y={12}
                      fill="white"
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      {minThreshold} hours
                    </text>
                  </g>
                )}
              />
            </BarChart>
          ) : (
            <SkeletonBarChart />
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const SkeletonBarChart: React.FC = () => {
  return (
  <div style={{ padding: "10px", background: "#f9f9f9", height: "100%" }}>
      <div
        className="px-1"
        style={{
          display: "flex",
          gap: "30px",
          width: "100%",
          height: "100%",
          paddingTop: "80px",
        }}
      >
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} height={90} width={45} />
      ))}
    </div>
  </div>
);
};

export default Activity;